import { useCallback, useState } from "react";
import { Flex, Text, IconButton, TextField } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import Select from "@/components/shared/select";
import postAnswerStyles from "./PostAnswerActionsConfiguration.module.scss";
import { TagInputProps } from "./PostAnswerActions.model";

export function TagInput({
    tags,
    onAddTag,
    onRemoveTag,
    enrichmentOptions,
}: TagInputProps) {
    const [newTag, setNewTag] = useState("");
    const [tagError, setTagError] = useState<string | null>(null);

    const handleAddTag = useCallback(() => {
        const normalizedTag = newTag.trim().replace(/\s+/g, " ");
        if (!normalizedTag) return;

        const result = onAddTag(normalizedTag);
        if (result.success) {
            setNewTag("");
            setTagError(null);
        } else {
            setTagError(result.error || "Failed to add tag");
        }
    }, [newTag, onAddTag]);

    const handleAddEnrichment = useCallback(
        (enrichmentId: string) => {
            const enrichment = enrichmentOptions.find((e) => e.value === enrichmentId);
            if (enrichment) {
                const result = onAddTag(`${enrichment.label}`);
                if (result.success) {
                    setTagError(null);
                } else {
                    setTagError(result.error || "Failed to add tag");
                }
            }
        },
        [enrichmentOptions, onAddTag]
    );

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleAddTag();
            }
        },
        [handleAddTag]
    );

    return (
        <Flex direction="column" gap="2" width="100%">
            <Flex gap="2" align="center">
                <TextField.Root
                    placeholder="Enter tag name..."
                    value={newTag}
                    onChange={(e) => {
                        setNewTag(e.target.value);
                        setTagError(null);
                    }}
                    onKeyDown={handleKeyPress}
                    className={postAnswerStyles.inputField}
                />
                <IconButton
                    size="2"
                    highContrast
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                >
                    <PlusIcon size={16} />
                </IconButton>
            </Flex>

            {enrichmentOptions.length > 0 && (
                <Flex direction="column" gap="1">
                    <Text size="1" color="gray">
                        Or select an enrichment to add as a tag:
                    </Text>
                    <Select
                        options={enrichmentOptions.map((e) => ({
                            value: e.value,
                            label: e.label,
                        }))}
                        value={undefined}
                        onChange={handleAddEnrichment}
                        placeholder="Select an enrichment..."
                        allowClear
                    />
                </Flex>
            )}

            {tagError && (
                <Text size="1" color="red">
                    {tagError}
                </Text>
            )}

            {tags.length > 0 && (
                <Flex gap="2" wrap="wrap" mt="2">
                    {tags.map((tag) => (
                        <Flex
                            key={tag}
                            align="center"
                            gap="1"
                            className={postAnswerStyles.tagChip}
                        >
                            <Text size="1">{tag}</Text>
                            <IconButton
                                color="gray"
                                variant="ghost"
                                size="1"
                                onClick={() => onRemoveTag(tag)}
                                className={postAnswerStyles.tagRemoveButton}
                            >
                                <PlusIcon
                                    size={12}
                                    className={postAnswerStyles.tagRemoveIcon}
                                />
                            </IconButton>
                        </Flex>
                    ))}
                </Flex>
            )}
        </Flex>
    );
}

